import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import CategoryTabs from "@/components/feature/CategoryTabs";
import FAQ from "@/components/feature/FAQ";
import SchemaOrg from "@/components/base/SchemaOrg";
import { articles, categories, faqs, siteInfo } from "@/mocks/siteData";

export default function Home() {
  const featuredArticles = articles.slice(0, 6);

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" />
      <SchemaOrg type="FAQPage" faqData={faqs} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20white%20silk%20stockings%20draped%20artistically%20on%20a%20minimalist%20cream%20colored%20background%2C%20soft%20natural%20daylight%2C%20luxury%20sensual%20textile%20photography%2C%20delicate%20folds%20and%20gentle%20highlights%2C%20cream%20and%20warm%20beige%20color%20palette%2C%20intimate%20atmospheric%20mood%2C%20shallow%20depth%20of%20field&width=1800&height=900&seq=hero-home&orientation=landscape"
            alt={siteInfo.name}
            title={siteInfo.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              {siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-2xl mb-8 leading-relaxed">
              {siteInfo.tagline} —— {siteInfo.description}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/category/material"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap"
              >
                浏览指南
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/20 text-white border border-white/30 rounded-full text-sm font-medium hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                探索分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                从白丝材质到互动技巧，覆盖白丝足交的方方面面
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-accent-300 transition-all duration-300"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      title={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-foreground-950 group-hover:text-accent-500 transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-600 mt-1 line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  精选文章
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  最新最全面的白丝足交指南内容
                </p>
              </div>
              <CategoryTabs />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  category={article.category}
                  imageUrl={article.imageUrl}
                />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/material"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-accent-500 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于白丝足交，你想知道的都在这里
              </p>
            </div>
            <FAQ items={faqs} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 px-6 md:px-10 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20dreamy%20white%20silk%20fabric%20texture%20background%2C%20cream%20and%20warm%20ivory%20gradient%2C%20minimalist%20elegant%20atmosphere%2C%20gentle%20flowing%20fabric%20folds%2C%20luxury%20textile%20photography%2C%20soft%20focus%2C%20intimate%20sensual%20mood&width=1800&height=600&seq=cta-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-50/60"></div>
          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              探索更多白丝足交的感官世界
            </h2>
            <p className="text-sm text-foreground-700 mb-6 leading-relaxed">
              从材质选择到互动技巧，持续更新专业、私密的白丝足交指南
            </p>
            <Link
              to="/category/material"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap"
            >
              开始阅读
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}